import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { logWarn } from '../lib/logger';
import { logReadFailure } from '../lib/net/logReadFailure';
import { useTelephonyStore } from '../store/telephonyStore';
import { usePeriodicRead } from './usePeriodicRead';

const POLL_MS = 60_000;

/** One caller to call back — row of RPC `get_missed_calls` (passed through as is). */
export interface MissedCall {
  caller_e164: string;
  last_call_id: string;
  last_missed_at: string;
  missed_count: number;
  voicemail_call_id: string | null;
  voicemail_transcript: string | null;
  contact_id: string | null;
  contact_name: string | null;
  account_id: string | null;
  account_name: string | null;
}

/**
 * Recent missed calls not yet called back. The rule (what "missed" and "called
 * back" mean) lives in the RPC; this hook only reads it. Light 60 s poll for the
 * header badge, plus a refresh when a call ends — a callback must drop its row.
 *
 * Untyped RPC calls, localized: the functions are in prod but not yet in the
 * generated types (same as useAwaitingDecisionsCount).
 */
export function useMissedCalls(enabled: boolean) {
  const [calls, setCalls] = useState<MissedCall[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const phase = useTelephonyStore((s) => s.phase);
  const previousPhase = useRef(phase);

  const refresh = useCallback(async () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error: rpcError } = await (supabase.rpc as any)('get_missed_calls', { p_days: 14, p_limit: 50 });
    setLoading(false);
    if (rpcError) {
      logReadFailure('media', 'telephony: get_missed_calls failed', rpcError);
      setError('Impossible de charger les appels manqués');
      return;
    }
    setError(null);
    setCalls((data ?? []) as MissedCall[]);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void refresh();
    const onFocus = () => void refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [enabled, refresh]);
  usePeriodicRead(() => void refresh(), enabled ? POLL_MS : null);

  // A softphone call just ended: the outbound row exists now → re-read.
  useEffect(() => {
    if (enabled && phase === 'idle' && previousPhase.current !== 'idle') void refresh();
    previousPhase.current = phase;
  }, [enabled, phase, refresh]);

  const markHandled = useCallback(
    async (callerE164: string) => {
      // Optimistic: the row leaves at once, the re-read restores it on failure.
      setCalls((prev) => prev.filter((c) => c.caller_e164 !== callerE164));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: rpcError } = await (supabase.rpc as any)('mark_missed_calls_handled', {
        p_caller_e164: callerE164,
      });
      if (rpcError) {
        logWarn('media', 'telephony: mark_missed_calls_handled failed', { err: rpcError });
        setError('Impossible de marquer comme traité');
        await refresh();
      }
    },
    [refresh],
  );

  return { calls, loading, error, refresh, markHandled };
}
